/**
 * Signed-in change-password (design 1C) on the real Auth contract
 * (ZOY-286, POST /identity/me/password): current + new + confirm, live
 * rules panel as slots from the clinic system, disabled-until-ready WITH the
 * reason in words beside it. A wrong current password is an INLINE error —
 * never a sign-out. Other sessions are revoked; only when the server says
 * this one was not retained does the page route back to sign-in.
 */

import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useIntl } from 'react-intl';
import s from './access.module.css';
import { Button, Notice, PasswordField, PasswordRulesPanel } from '@/ui/kit';
import { AuthCode, changeOwnPassword } from '@/core/auth/api';
import { useSession } from '@/core/auth/SessionProvider';
import { ApiError, NetworkError } from '@/core/api/errors';
import { passwordRules, rulesSatisfied } from './passwordRules';
import { ruleRows } from './passwordRuleRows';

export function ChangePasswordPage() {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  const navigate = useNavigate();
  const { signOut } = useSession();

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [state, setState] = useState<'form' | 'done' | 'signed-out'>('form');
  const [currentError, setCurrentError] = useState<string | null>(null);
  const [nextError, setNextError] = useState<string | null>(null);
  const [banner, setBanner] = useState<{ kind: 'warn' | 'err'; text: string } | null>(null);

  const mismatch = confirm !== '' && confirm !== next;
  // ZOY-632: the real Auth rules, checked live; the button waits for them too.
  const rules = passwordRules(next, current);
  const ready = current !== '' && next !== '' && confirm === next && rulesSatisfied(rules);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setCurrentError(null);
    setNextError(null);
    setBanner(null);
    try {
      const result = await changeOwnPassword(current, next);
      if (result.currentSessionRetained) {
        setState('done');
      } else {
        // The server revoked this session as well: local material is stale.
        setState('signed-out');
        await signOut();
        void navigate({ to: '/signin' });
      }
    } catch (err) {
      if (err instanceof NetworkError) {
        setBanner({ kind: 'warn', text: t('signin.netBody') });
        return;
      }
      if (err instanceof ApiError) {
        const code = err.authCode;
        if (code === AuthCode.CurrentPasswordIncorrect) {
          setCurrentError(t('change.wrongCurrent'));
          return;
        }
        if (code === AuthCode.PasswordUnchanged) {
          setNextError(t('change.unchanged'));
          return;
        }
        if (code === AuthCode.PasswordPolicyRejected || err.status === 400) {
          // Named in the reader's language; the panel above shows which rule fails.
          setNextError(t('signin.setNewRejected'));
          return;
        }
        if (code === AuthCode.PasswordNotApplicable) {
          setBanner({ kind: 'warn', text: t('change.notApplicable') });
          return;
        }
        if (err.status === 503 || code === AuthCode.PasswordChangeUnavailable) {
          setBanner({ kind: 'warn', text: t('change.unavailable') });
          return;
        }
        if (err.accountState !== undefined) {
          setBanner({ kind: 'err', text: t('signin.inactiveBody') });
          return;
        }
      }
      setBanner({ kind: 'err', text: t('common.failedKept') });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className={s.pageHead}>
        <h1 className={s.pageTitle}>{t('change.title')}</h1>
        <p className={s.pageSub}>{t('change.sub')}</p>
      </div>

      <div className={s.sectionCard}>
        {state === 'form' && (
          <form className={s.formCol} onSubmit={submit}>
            {banner && (
              <Notice kind={banner.kind} role="alert">
                {banner.text}
              </Notice>
            )}
            <PasswordField
              label={t('change.current')}
              value={current}
              onChange={(v) => {
                setCurrent(v);
                setCurrentError(null);
              }}
              help={t('change.currentHelp')}
              error={currentError ?? undefined}
              requirement={{ word: t('signin.required'), kind: 'required' }}
              autoComplete="current-password"
              showWord="show"
              hideWord="hide"
            />
            <div>
              <PasswordField
                label={t('forgot.newPwLabel')}
                value={next}
                onChange={(v) => {
                  setNext(v);
                  setNextError(null);
                }}
                help={t('forgot.newPwHelp')}
                error={nextError ?? undefined}
                requirement={{ word: t('signin.required'), kind: 'required' }}
                autoComplete="new-password"
                showWord="show"
                hideWord="hide"
              />
              <PasswordRulesPanel
                title={t('forgot.rulesTitle')}
                rules={ruleRows(intl, rules, 'current')}
                stateWords={{
                  pending: t('pw.rulePending'),
                  met: t('pw.ruleMet'),
                  unmet: t('pw.ruleUnmet'),
                }}
                note={t('forgot.rulesNote')}
              />
            </div>
            <PasswordField
              label={t('forgot.confirmLabel')}
              value={confirm}
              onChange={setConfirm}
              help={t('forgot.confirmHelp')}
              requirement={{ word: t('signin.required'), kind: 'required' }}
              error={mismatch ? t('forgot.mismatch') : undefined}
              ok={confirm !== '' && !mismatch ? t('forgot.matchOk') : undefined}
              autoComplete="new-password"
              showWord="show"
              hideWord="hide"
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <Button type="submit" variant="brand" disabled={!ready || busy} busy={busy}>
                {t('change.cta')}
              </Button>
              {!ready && (
                <span style={{ fontSize: 12.5, color: 'var(--zc-muted)' }}>
                  {t('change.ready')}
                </span>
              )}
            </div>
          </form>
        )}
        {state === 'done' && (
          <div className={s.formCol}>
            <Notice kind="ok" title={t('forgot.doneTitle')}>
              {t('change.doneBody')}
            </Notice>
            <div>
              <Button variant="quiet" onClick={() => void navigate({ to: '/account' })}>
                {t('common.back')}
              </Button>
            </div>
          </div>
        )}
        {state === 'signed-out' && (
          <div className={s.formCol}>
            <Notice kind="ok" title={t('forgot.doneTitle')}>
              {t('change.doneSignedOut')}
            </Notice>
          </div>
        )}
      </div>
    </div>
  );
}
